"use client";

import { ReactNode, useEffect, useState } from "react";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { cn } from "@/lib/utils";
import { SliderWrapper } from "@/components/common/slider-wrapper";

export const CardSlider = ({
  items,
  className = "@container my-6",
  basis = "basis-[280px] md:basis-[300px]",
  alwaysToLeft = false,
  carouselContentClassName = "",
  setApi: externalSetApi,
  centerArrows = false,
  startIndex = 0,
  align,
  focusIndex,
}: {
  items: ReactNode[];
  className?: string;
  basis?: "basis-[280px] md:basis-[300px]" | "basis-[280px]" | string;
  alwaysToLeft?: boolean;
  carouselContentClassName?: string;
  setApi?: (api: CarouselApi) => void;
  centerArrows?: boolean;
  // slajd, na którym karuzela ma wystartować (embla pozycjonuje go wg align)
  startIndex?: number;
  // jak embla wyrównuje slajd (domyślnie "center")
  align?: "start" | "center" | "end";
  // slajd, do którego po załadowaniu płynnie przewijamy, jeśli jest poza widokiem
  focusIndex?: number;
}) => {
  const [api, setApi] = useState<CarouselApi | null>(null);

  const handleSetApi = (newApi: CarouselApi) => {
    setApi(newApi);
    if (externalSetApi) {
      externalSetApi(newApi);
    }
  };

  // Po załadowaniu: jeśli aktywna karta jest poza widokiem (mobile), przewiń do niej
  // płynnie. Gdy już widoczna (zwykle desktop), nie ruszamy karuzeli. Re-run na reInit,
  // bo pierwszy pomiar (szerokość 0 / lazy obrazki) potrafi uznać wszystkie karty za widoczne.
  useEffect(() => {
    if (!api || focusIndex === undefined) return;

    const alignToFocus = () => {
      if (!api.slidesInView().includes(focusIndex)) {
        api.scrollTo(focusIndex);
      }
    };

    // pierwsze wywołanie po klatce — embla musi zdążyć zmierzyć szerokość,
    // inaczej slidesInView() zwraca wszystkie karty jako widoczne i nic nie robimy
    const raf = requestAnimationFrame(alignToFocus);
    api.on("reInit", alignToFocus);

    return () => {
      cancelAnimationFrame(raf);
      api.off("reInit", alignToFocus);
    };
  }, [api, focusIndex]);

  return (
    <div className={className}>
      <div
        className={cn(
          "sm:overflow-hidden @min-[700px]:mx-auto",
          alwaysToLeft && "@min-[700px]:mx-0",
        )}
      >
        <SliderWrapper api={api} centerArrows={centerArrows}>
          <Carousel
            opts={{
              skipSnaps: true,
              startIndex,
              ...(align ? { align } : {}),
              // dla auto-scrollu do focusIndex liczymy tylko w pełni widoczne karty,
              // by karta wystająca zza krawędzi nie blokowała przewinięcia
              ...(focusIndex !== undefined ? { inViewThreshold: 0.99 } : {}),
            }}
            setApi={handleSetApi}
            plugins={[WheelGesturesPlugin()]}
          >
            <CarouselContent className={carouselContentClassName}>
              {items.map((item, index) => (
                <CarouselItem
                  className={cn(basis)}
                  isFirst={index === 0}
                  key={index}
                  isLast={index === items.length - 1}
                >
                  {item}
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </SliderWrapper>
      </div>
    </div>
  );
};
